import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Card, Flex, Heading, Text } from '@radix-ui/themes'
import { ApproveCompatibilityForm } from '~/features/meter-photos/components/ApproveCompatibilityForm'
import { MeterPhotoGrid } from '~/features/meter-photos/components/MeterPhotoGrid'
import { SuggestionPanel } from '~/features/meter-photos/components/SuggestionPanel'
import type { ReviewItem } from '~/features/meter-photos/labels'
import { formatTimeAgo } from '~/utils/date'
import { useTRPC } from '~/utils/trpc'

const ReviewCard = ({ item }: { item: ReviewItem }) => (
  <Card>
    <Flex direction="column" gap="3">
      <Flex align="baseline" justify="between" gap="3" wrap="wrap">
        <Flex direction="column">
          <Link to="/installations/$installationId" params={{ installationId: String(item.installation_id) }}>
            <Heading as="h2" size="3">
              {item.name ?? `Installation #${item.installation_id}`}
            </Heading>
          </Link>
          <Text size="2" color="gray">
            {[item.org?.name, item.site?.name].filter(Boolean).join(' · ')}
          </Text>
        </Flex>
        {item.photos[0] ? (
          <Text size="1" color="gray">
            Last photo {formatTimeAgo(item.photos[0].received_at)}
          </Text>
        ) : null}
      </Flex>
      <MeterPhotoGrid photos={item.photos} />
      <SuggestionPanel installation={item} hasPhotos={item.photos.length > 0} />
      <ApproveCompatibilityForm key={item.suggested_at ?? 'none'} installation={item} />
    </Flex>
  </Card>
)

/** Installations whose meter photos arrived and whose compatibility nobody has approved yet. */
const CompatibilityReviewPage = () => {
  const trpc = useTRPC()
  const review = useQuery(trpc.ops.compatibilityReview.list.queryOptions())

  return (
    <Flex direction="column" gap="4">
      <Flex align="baseline" gap="3">
        <Heading size="6">Compatibility review</Heading>
        {review.data ? <Text color="gray">{review.data.length} to approve</Text> : null}
      </Flex>
      {review.isLoading ? (
        <Text color="gray">Loading…</Text>
      ) : review.error ? (
        <Text color="red">{review.error.message}</Text>
      ) : (review.data ?? []).length === 0 ? (
        <Text color="gray">Nothing to approve.</Text>
      ) : (
        <Flex direction="column" gap="3">
          {(review.data ?? []).map((item) => (
            <ReviewCard key={item.installation_id} item={item} />
          ))}
        </Flex>
      )}
    </Flex>
  )
}

export const Route = createFileRoute('/compatibility-review/')({ component: CompatibilityReviewPage })
